import React, { useState, Fragment, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import { getDrawingBox } from "reduxModules/ducks/ui";
import { capitalize, has, isEmpty, minBy } from "lodash";
import { getAreaArray, getCurrentValue2 } from "reduxModules/selectors/containerSelectors";
import { closest } from "utilities/mathematic";
import { isInside } from "utilities/geometric";
import audioFileLinks from "globalConstants/AudioLinks";
import { AudioPlayerProps } from "types/components/elements";
import { RootState } from "types/redux/state";

/**
 * MUI AudioPlayer component that replaces the styled-components AudioPlayer component
 * 
 * This component provides a Material UI equivalent of the original AudioPlayer component
 * with similar props and functionality.
 * 
 * @returns {JSX.Element} MUI AudioPlayer component
 */
const MuiAudioPlayer: React.FC<AudioPlayerProps> = ({ anchor }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { bbox: { x0, y0 }, scale } = useSelector(getDrawingBox);
  const microphones = useSelector((state: RootState) => getCurrentValue2(state, 'microphones.layout'));
  const acoustics = useSelector((state: RootState) => getCurrentValue2(state, 'acoustics'));
  const { location, dimensions } = useSelector(getAreaArray)[0];
  const [source, setSource] = useState<string | null>(null);

  // Stop and reset the audio player if it exists
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    // get the position and corresponding audio file link
    const x = (anchor.x - x0) / scale;
    const y = (anchor.y - y0) / scale;
    const inArea = isInside([x, y], { ...location, ...dimensions });
    // Set the source 
    setSource(null);
    if (inArea) {
      const closestDevice = isEmpty(microphones) ? null : minBy(microphones, (device: any) => Math.hypot(device.location.x - x, device.location.y - y));
      const distance = closestDevice ? Math.hypot(closestDevice.location.x - x, closestDevice.location.y - y) : 0;
      const linkId = `${capitalize(acoustics)}-${closest([-100, 1, 2, 3, 4, 5, 7.5, 10], distance)}M.wav`;
      setSource(has(audioFileLinks, linkId) ? `${audioFileLinks[linkId as keyof typeof audioFileLinks]}/${linkId}` : null);
    }
  }, [anchor, x0, y0, scale, microphones, acoustics, location, dimensions]);

  useEffect(() => {
    setSource(null);
  }, []);

  // Also stop and reset audio when component unmounts
  useEffect(() => {
    setSource(null);
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  return (
    <Fragment>
      {source && (
        <AudioPlayerWrapper x={anchor.x} y={anchor.y}>
          <AudioPlayerContainer>
            <audio
              ref={audioRef}
              controls={true}
              src={`https://support.biamp.com/@api/deki/files/${source}`}
            >
              Error: Your browser does not support HTML5 audio playback.
            </audio>
          </AudioPlayerContainer>
        </AudioPlayerWrapper>
      )}
    </Fragment>
  );
};

// Define types for styled components props
interface AudioPlayerWrapperProps {
  x: number;
  y: number;
}

// Styled components using MUI styled API
const AudioPlayerWrapper = styled('foreignObject')<AudioPlayerWrapperProps>(({ x, y }) => ({
  width: 304,
  height: 70,
  transform: 'translate(-40, 0)',
  x,
  y
}));

const AudioPlayerContainer = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 304,
  height: 58,
  position: 'relative',
  marginTop: 10,
  border: '2px solid #000',
  borderRadius: 50,
  backgroundColor: '#fff',
  fontSize: 14,
  textAlign: 'center',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: -24,
    left: 28,
    borderWidth: 12,
    borderStyle: 'solid',
    borderColor: 'transparent transparent #000 transparent',
  }
});

export default MuiAudioPlayer;
